import {
  EditReleasePayload,
  GetExtendedReleasesResponse,
  GetReleasesResponse,
} from '@upright/common/model/releases/api-types';
import { ReleaseAndVersion } from '@upright/common/model/releases/types';
import { SuccessOrError } from '@upright/common/types-api';
import { loggedInUser } from '@upright/ui/untidy/redux/selectors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { fetchOrThrow } from '../../common/fetchers/fetch-or-throw';

const FETCH_EXTENDED_RELEASES = 'getExtendedReleases';

async function getAvailableReleases() {
  return await fetchOrThrow<GetReleasesResponse>('/releases', 'GET');
}

async function getExtendedReleases() {
  return await fetchOrThrow<GetExtendedReleasesResponse>('/releases/extended', 'GET');
}

async function editRelease(release: string, payload: EditReleasePayload) {
  return await fetchOrThrow<SuccessOrError>(`/release/${release}`, 'PATCH', payload);
}

async function publishModelRelease(release: string) {
  return await fetchOrThrow<string>(`/release/${release}/publish`, 'PATCH');
}

async function stabilizeModelRelease(release: string) {
  return await fetchOrThrow<string>(`/release/${release}/stabilize`, 'PATCH');
}

export function useGetAvailableReleases<T = GetReleasesResponse>(select?: (response: GetReleasesResponse) => T) {
  const user = useSelector(loggedInUser);
  return useQuery({
    queryKey: ['getAvailableReleases', user?.id],
    queryFn: getAvailableReleases,
    select,
    refetchOnMount: false,
  });
}

export function useGetExtendedReleases<T = GetExtendedReleasesResponse>(
  select?: (response: GetExtendedReleasesResponse) => T,
) {
  return useQuery({
    queryKey: [FETCH_EXTENDED_RELEASES],
    queryFn: getExtendedReleases,
    select,
  });
}

export function useEditRelease() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: EditReleasePayload & { release: string }) => {
      const { release, ...payload } = params;
      return await editRelease(release, payload);
    },

    onSettled: () => {
      void queryClient.refetchQueries({
        queryKey: [FETCH_EXTENDED_RELEASES],
      });
    },
  });
}

export function usePublishModelRelease() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (release: string) => {
      return await publishModelRelease(release);
    },

    onSettled: () => {
      void queryClient.refetchQueries({
        queryKey: [FETCH_EXTENDED_RELEASES],
      });
    },

    onError: error => {
      alert(error.message);
    },
  });
}

export function useStabilizeModelRelease() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (release: string) => {
      return await stabilizeModelRelease(release);
    },

    onSettled: () => {
      void queryClient.refetchQueries({
        queryKey: [FETCH_EXTENDED_RELEASES],
      });
    },

    onError: error => {
      alert(error.message);
    },
  });
}

async function getDefaultRelease() {
  return await fetchOrThrow<ReleaseAndVersion>(`/release/default`);
}

export function useGetDefaultRelease() {
  return useQuery({
    queryKey: ['getDefaultRelease'],
    queryFn: getDefaultRelease,
  });
}
